// src/services/JobsService.ts
import { jobSearch } from './api.ts';

export interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  salary_min?: number;
  salary_max?: number;
  date_posted: string;
  job_type?: string;
  contacts_count?: number;
  match_percentage: number;
  job_url?: string;
  site?: string;
  is_remote?: boolean;
  description?: string;
  currency?: string;
  salary_source?: string;
}

export interface JobFilters {
  location: string;
  jobType: string;
  salary: string;
  datePosted: string;
}

export interface SearchStats {
  count: number;
  lastUpdated: string;
}

export interface JobSearchParams {
  searchId: string;
  filters: JobFilters;
}

export interface UserSearch {
  user_search_query_id: string;
  search_query_id?: string;
  query?: string;
  original_query?: string;
  job_title?: string;
  location?: string;
  is_remote?: boolean;
  slug?: string;
  total_jobs: number;
  created_at: string;
  has_results: boolean;
  status?: string; // Task status: pending, processing, completed, failed
}

export class JobsService {
  private static instance: JobsService;
  private allJobs: Job[] = [];
  private filteredJobs: Job[] = [];
  private lastSearchKey: string = '';
  private lastSearchId: string = '';
  private lastSearchTimestamp: string = '';
  private userSearchesCache: UserSearch[] = [];
  private searchResultsCache: { [searchId: string]: Job[] } = {};

  private constructor() { }

  static getInstance(): JobsService {
    if (!JobsService.instance) {
      JobsService.instance = new JobsService();
    }
    return JobsService.instance;
  }

  /**
   * Load jobs for a specific user search
   */
  async loadSearchResults(params: JobSearchParams): Promise<{
    jobs: Job[];
    stats: SearchStats;
  }> {
    const { searchId, filters } = params;

    // Get the search timestamp from userSearchesCache
    const searchMetadata = this.userSearchesCache.find(s => s.user_search_query_id === searchId);
    if (searchMetadata?.created_at) {
      console.log('📅 Raw created_at from API:', searchMetadata.created_at);

      // Parse the timestamp - if it doesn't have timezone info, assume UTC
      let createdDate = new Date(searchMetadata.created_at);

      // If the timestamp doesn't include 'Z' or timezone offset, it might be interpreted as local time
      // Force UTC parsing if no timezone indicator
      if (!searchMetadata.created_at.includes('Z') && !searchMetadata.created_at.includes('+')) {
        createdDate = new Date(searchMetadata.created_at + 'Z');
      }

      console.log('🕐 Parsed date object:', createdDate);
      console.log('🌍 Local time string:', createdDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

      this.lastSearchTimestamp = createdDate.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    // Check if we already have results for this searchId
    const cached = this.searchResultsCache[searchId];
    if (cached && cached.length) {
      this.allJobs = cached;
      this.filteredJobs = this.applyFilters(cached, filters);
      this.lastSearchId = searchId;
      this.lastSearchKey = `${searchId}-${filters.location}-${filters.jobType}-${filters.salary}-${filters.datePosted}`;
      console.log(`♻️ Using cached search results for ${searchId}: ${cached.length} total, ${this.filteredJobs.length} filtered`);
      return {
        jobs: this.filteredJobs,
        stats: this.generateSearchStats()
      };
    }

    try {
      // Fetch jobs for this specific search ID
      const response = await jobSearch.getSearchResults(searchId);

      // Handle both { jobs: [...] } and [...] response formats
      let allJobs: Job[] = [];
      if (Array.isArray(response)) {
        allJobs = response;
      } else if (response && Array.isArray(response.jobs)) {
        allJobs = response.jobs;
      } else {
        console.warn(`⚠️ Unexpected response format for search ${searchId}:`, response);
        allJobs = [];
      }

      console.log(`📥 Raw search results for ${searchId}:`, response);

      // Store all jobs and apply filters
      this.allJobs = allJobs;
      this.filteredJobs = this.applyFilters(allJobs, filters);
      this.lastSearchKey = `${searchId}-${filters.location}-${filters.jobType}-${filters.salary}-${filters.datePosted}`;
      this.lastSearchId = searchId;
      this.searchResultsCache[searchId] = allJobs;

      console.log(`🔍 Search results loaded: ${allJobs.length} total, ${this.filteredJobs.length} filtered`);

      if (allJobs.length === 0) {
        console.warn(`⚠️ No jobs found for search ${searchId} - search may still be processing`);
      }

      return {
        jobs: this.filteredJobs,
        stats: this.generateSearchStats()
      };
    } catch (error: any) {
      console.error('❌ JobsService: Failed to load search results:', error);
      console.error('Error details:', error?.response?.data);
      // Don't throw - return empty results to allow UI to show "processing" state
      return {
        jobs: [],
        stats: { count: 0, lastUpdated: new Date().toISOString() }
      };
    }
  }

  /**
   * Whether a particular search's base results are already loaded
   */
  hasSearchLoaded(searchId: string): boolean {
    return !!(this.searchResultsCache[searchId] && this.searchResultsCache[searchId].length);
  }

  /**
   * Get results from cache for a search and make them current; returns null if not cached
   */
  getCachedResultsFor(searchId: string, filters: JobFilters): { jobs: Job[]; stats: SearchStats } | null {
    const cached = this.searchResultsCache[searchId];
    if (!cached || !cached.length) return null;

    // Get the search timestamp from userSearchesCache
    const searchMetadata = this.userSearchesCache.find(s => s.user_search_query_id === searchId);
    if (searchMetadata?.created_at) {
      console.log('📅 Raw created_at from API:', searchMetadata.created_at);

      // Parse the timestamp - if it doesn't have timezone info, assume UTC
      let createdDate = new Date(searchMetadata.created_at);

      // If the timestamp doesn't include 'Z' or timezone offset, it might be interpreted as local time
      // Force UTC parsing if no timezone indicator
      if (!searchMetadata.created_at.includes('Z') && !searchMetadata.created_at.includes('+')) {
        createdDate = new Date(searchMetadata.created_at + 'Z');
      }

      console.log('🕐 Parsed date object:', createdDate);
      console.log('🌍 Local time string:', createdDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

      this.lastSearchTimestamp = createdDate.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    this.allJobs = cached;
    this.filteredJobs = this.applyFilters(cached, filters);
    this.lastSearchId = searchId;
    this.lastSearchKey = `${searchId}-${filters.location}-${filters.jobType}-${filters.salary}-${filters.datePosted}`;
    return {
      jobs: this.filteredJobs,
      stats: this.generateSearchStats()
    };
  }

  /**
   * Get user's search history
   */
  async getUserSearches(): Promise<UserSearch[]> {
    // Return cached searches if available
    if (this.userSearchesCache.length) {
      return this.userSearchesCache;
    }
    try {
      const response = await jobSearch.getUserSearches();
      console.log('📡 Raw API response for getUserSearches:', response);
      const searches = response.searches || [];
      console.log(`📋 All searches from API: ${searches.length}`, searches);

      // Show all searches - including ones that are still processing
      // This way users can see their searches even if results aren't ready yet
      const allSearches = searches;

      const completedWithResults = searches.filter((s: UserSearch) => s.has_results && s.total_jobs > 0);
      const processing = searches.filter((s: UserSearch) => !s.has_results && s.total_jobs === 0);

      console.log(`✅ Completed searches with results: ${completedWithResults.length}`, completedWithResults);
      console.log(`⏳ Processing/pending searches: ${processing.length}`, processing.map((s: UserSearch) => ({
        query: s.query || s.original_query,
        has_results: s.has_results,
        total_jobs: s.total_jobs,
        status: s.status,
        created_at: s.created_at
      })));

      this.userSearchesCache = allSearches; // Cache all searches, not just completed ones
      return allSearches;
    } catch (error) {
      console.error('JobsService: Failed to load user searches:', error);
      throw error;
    }
  }

  /** Returns true if user searches are already cached */
  hasUserSearches(): boolean {
    return this.userSearchesCache.length > 0;
  }

  /** Returns cached user searches without fetching */
  getCachedUserSearches(): UserSearch[] {
    return this.userSearchesCache;
  }

  /** Returns the last active search id (if any) */
  getActiveSearchId(): string {
    return this.lastSearchId;
  }

  /**
   * Apply filters to job list
   */
  private applyFilters(jobs: Job[], filters: JobFilters): Job[] {
    let filtered = [...jobs];

    // Apply location filter
    if (filters.location) {
      filtered = filtered.filter(job => {
        if (filters.location === 'remote') return job.is_remote;
        if (filters.location === 'hybrid') return job.location?.toLowerCase().includes('hybrid');
        return job.location?.toLowerCase().includes(filters.location.replace('-', ' '));
      });
    }

    // Apply job type filter (normalize both job data and filter)
    if (filters.jobType) {
      const wanted = this.normalizeJobType(filters.jobType);
      filtered = filtered.filter(job => {
        const jobKey = this.normalizeJobType(job.job_type || '');
        return jobKey === wanted;
      });
    }

    // Apply salary filter
    if (filters.salary) {
      const minSalary = parseInt(filters.salary.replace('k', '')) * 1000;
      filtered = filtered.filter(job =>
        (job.salary_min && job.salary_min >= minSalary) ||
        (job.salary_max && job.salary_max >= minSalary)
      );
    }

    // Apply date filter
    if (filters.datePosted) {
      const daysBack = parseInt(filters.datePosted.replace('d', ''));
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysBack);

      filtered = filtered.filter(job => {
        const jobDate = new Date(job.date_posted);
        return jobDate >= cutoffDate;
      });
    }

    console.log(`🔍 Filter applied: ${jobs.length} → ${filtered.length} jobs`);
    return filtered;
  }

  /**
   * Normalize various job type strings into canonical keys used by filters/UI
   * Examples:
   *  - "Full-time", "full time", "permanent", "regular" -> "full-time"
   *  - "Part-time", "part time", "pt" -> "part-time"
   *  - "Contract", "contractor", "temp", "temporary", "term" -> "contract"
   *  - "Intern", "internship", "co-op", "coop" -> "internship"
   *  - "freelance", "consultant", "gig" -> "freelance"
   */
  private normalizeJobType(input: string): string {
    const raw = (input || '').toString().toLowerCase();
    // strip non-alphanumeric to make matching robust
    const key = raw.replace(/[^a-z0-9]/g, '');

    if (!key) return '';

    if (key.includes('permanent') || key.includes('regular') || key.includes('fulltime') || key === 'ft' || key.includes('fullt')) {
      return 'full-time';
    }
    if (key.includes('parttime') || key === 'pt' || key.includes('partt')) {
      return 'part-time';
    }
    if (key.includes('intern') || key.includes('coop') || key.includes('co-op')) {
      return 'internship';
    }
    if (key.includes('contract') || key.includes('temp') || key.includes('term')) {
      return 'contract';
    }
    if (key.includes('freelance') || key.includes('consult') || key.includes('gig')) {
      return 'freelance';
    }

    // Fallback: classify common exacts
    switch (key) {
      case 'full':
        return 'full-time';
      case 'part':
        return 'part-time';
      case 'intern':
        return 'internship';
      default:
        return raw.includes('full') ? 'full-time' : raw.includes('part') ? 'part-time' : raw.includes('intern') ? 'internship' : raw.includes('temp') ? 'contract' : raw.includes('contract') ? 'contract' : '';
    }
  }

  /**
   * Update filters and return filtered results
   */
  updateFilters(filters: JobFilters): Job[] {
    this.filteredJobs = this.applyFilters(this.allJobs, filters);
    console.log(`🔍 Filters updated: ${this.allJobs.length} → ${this.filteredJobs.length} jobs`);
    return this.filteredJobs;
  }

  /**
   * Get paginated results
   */
  getPaginatedJobs(page: number, pageSize: number): {
    jobs: Job[];
    hasMore: boolean;
    totalCount: number;
  } {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedJobs = this.filteredJobs.slice(startIndex, endIndex);

    return {
      jobs: paginatedJobs,
      hasMore: endIndex < this.filteredJobs.length,
      totalCount: this.filteredJobs.length
    };
  }

  /**
   * Get all filtered jobs (for infinite scroll)
   */
  getJobsUpToPage(page: number, pageSize: number): Job[] {
    const endIndex = page * pageSize;
    return this.filteredJobs.slice(0, endIndex);
  }

  /**
   * Generate search statistics
   */
  private generateSearchStats(): SearchStats {
    return {
      count: this.filteredJobs.length,
      lastUpdated: this.lastSearchTimestamp
    };
  }

  /**
   * Get current filtered jobs count
   */
  getFilteredCount(): number {
    return this.filteredJobs.length;
  }

  /**
   * Get current total jobs count
   */
  getTotalCount(): number {
    return this.allJobs.length;
  }

  /**
   * Get current jobs cache (filtered if available, else all)
   */
  getCurrentJobs(): Job[] {
    return this.filteredJobs.length ? this.filteredJobs : this.allJobs;
  }

  /**
   * Clear cache (useful when switching resumes)
   */
  clearCache(): void {
    this.allJobs = [];
    this.filteredJobs = [];
    this.lastSearchKey = '';
    this.lastSearchId = '';
    this.lastSearchTimestamp = '';
    this.searchResultsCache = {};
    // Keep userSearchesCache to avoid refetching history unless explicitly reset
    console.log('🗑️ JobsService cache cleared');
  }

  /**
   * Invalidate user searches cache (useful after creating a new search)
   */
  invalidateSearchesCache(): void {
    this.userSearchesCache = [];
    console.log('🗑️ User searches cache invalidated');
  }

  /**
   * Check if filters have any active values
   */
  hasActiveFilters(filters: JobFilters): boolean {
    return !!(filters.location || filters.jobType || filters.salary || filters.datePosted);
  }

  /**
   * Get empty filters object
   */
  getEmptyFilters(): JobFilters {
    return {
      location: '',
      jobType: '',
      salary: '',
      datePosted: ''
    };
  }
}

export default JobsService;